/**
 * Umschalter mit wenigen, gleichrangigen Möglichkeiten — Tag · Woche · Monat.
 *
 * Übernommen aus `components/navigation/SegmentedControl.jsx`, dort aus `<div>`
 * mit `onClick` gebaut. Hier ist es eine **Radiogruppe**: echte Knöpfe in einem
 * `role="radiogroup"`, mit `aria-checked` und wanderndem Tabulatorstopp. Die
 * Pfeiltasten wechseln die Auswahl, der Tabulator springt aus der Gruppe heraus
 * statt durch jede Schaltfläche — so verhält sich eine Radiogruppe überall
 * sonst, und wer sie mit der Tastatur bedient, erwartet nichts anderes.
 *
 * Für mehr als vier oder fünf Möglichkeiten ist das die falsche Komponente: Die
 * Knöpfe stehen nebeneinander und werden dann schmaler als ihre Beschriftung.
 */
import { useRef, type KeyboardEvent } from "react";
import { fontWeight } from "../tokens.js";

export interface SegmentedOption<T extends string> {
  readonly value: T;
  readonly label: string;
}

export interface SegmentedControlProps<T extends string> {
  readonly options: readonly SegmentedOption<T>[];
  readonly value: T;
  readonly onChange: (value: T) => void;
  /** Wofür die Gruppe steht — „Zeitraum". Für die Sprachausgabe. */
  readonly label: string;
  readonly size?: "sm" | "md";
  /**
   * Füllt die Breite des Elternelements und teilt sie gleichmäßig auf — für
   * eine Wahl in einem schmalen Kasten wie dem Kontomenü (HM-107). Ohne die
   * Angabe ist die Gruppe so breit wie ihre Beschriftungen, wie in einer
   * Kopfzeile.
   */
  readonly fill?: boolean;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  label,
  size = "md",
  fill = false,
}: SegmentedControlProps<T>) {
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const height = size === "sm" ? 28 : 34;

  function move(event: KeyboardEvent, index: number): void {
    const step =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? -1
          : 0;
    if (step === 0) return;

    event.preventDefault();
    // Umlaufend: Vom letzten Eintrag geht es weiter zum ersten.
    const next = (index + step + options.length) % options.length;
    const option = options[next];
    if (option === undefined) return;

    onChange(option.value);
    buttons.current[next]?.focus();
  }

  return (
    <div
      role="radiogroup"
      aria-label={label}
      style={{
        display: fill ? "flex" : "inline-flex",
        width: fill ? "100%" : undefined,
        gap: 2,
        padding: 2,
        borderRadius: "var(--radius-md)",
        background: "var(--surface-sunken)",
        border: "1px solid var(--border-subtle)",
      }}
    >
      {options.map((option, index) => {
        const selected = option.value === value;

        return (
          <button
            key={option.value}
            ref={(element) => {
              buttons.current[index] = element;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            /**
             * Nur der ausgewählte Eintrag ist mit dem Tabulator erreichbar.
             * Innerhalb der Gruppe wird mit den Pfeiltasten gewechselt.
             */
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => move(event, index)}
            style={{
              height,
              padding: fill ? "0 6px" : size === "sm" ? "0 10px" : "0 14px",
              flex: fill ? "1 1 0" : undefined,
              whiteSpace: "nowrap",
              border: "none",
              borderRadius: "var(--radius-sm)",
              cursor: "pointer",
              fontFamily: "var(--font-body)",
              fontSize: size === "sm" ? 12.5 : 13.5,
              fontWeight: selected ? fontWeight.semibold : fontWeight.medium,
              color: selected ? "var(--text-strong)" : "var(--text-muted)",
              background: selected ? "var(--surface-card)" : "transparent",
              boxShadow: selected ? "var(--shadow-xs)" : "none",
              transition: "background .15s ease, color .15s ease",
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
